import { useState } from 'react';
import { usePolling } from './use-polling';
import { getTrialGeneration } from '../actions';
import { ETrialGenerationStatus, type TrialGenerationStatus } from '../types';

export const GENERATION_POLL_INTERVAL_MS = 2000;

/** The lead may enter the product from gate_done on; enrich keeps running in the background. */
export const isGateFinished = (status?: ETrialGenerationStatus | null) => (
  !!status && [
    ETrialGenerationStatus.GATE_DONE,
    ETrialGenerationStatus.ENRICH_RUNNING,
    ETrialGenerationStatus.ENRICH_DONE,
    ETrialGenerationStatus.ENRICH_PARTIAL,
  ].includes(status)
);

/**
 * Polls the generation status of a trial organisation until the gate has
 * finished or failed. `initial` lets the signup response seed the screen so
 * the first paint already shows steps.
 */
export const useTrialGeneration = (organisationId: string, initial: TrialGenerationStatus | null = null) => {
  const [generation, setGeneration] = useState<TrialGenerationStatus | null>(initial);
  const [failedRequests, setFailedRequests] = useState(0);

  const status = generation?.status ?? null;
  const isFinished = isGateFinished(status) || status === ETrialGenerationStatus.GATE_FAILED;

  usePolling(async (signal) => {
    try {
      const { data } = await getTrialGeneration(organisationId);

      if (signal.cancelled || !data) return;

      setGeneration(data);
      setFailedRequests(0);
    } catch (err) {
      if (signal.cancelled) return;

      setFailedRequests((count) => count + 1);
    }
  }, GENERATION_POLL_INTERVAL_MS, !!organisationId && !isFinished);

  return {
    generation,
    status,
    isReady: isGateFinished(status),
    isFailed: status === ETrialGenerationStatus.GATE_FAILED,
    // The API is unreachable for a while; the screen can say so instead of spinning forever.
    isStalled: failedRequests >= 5,
  };
};
